import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { X, Camera, Plus, LogOut, ChevronUp, ChevronDown } from "lucide-react";
import { setUser, clearUser } from "../store/userSlice";
import API from "../api/api";
import "../style/account-switcher.css";

export default function AccountSwitcherModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);

  const [savedAccounts, setSavedAccounts] = useState([]);
  const [showMoreAccounts, setShowMoreAccounts] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Sync current user into savedAccounts list in localStorage
  useEffect(() => {
    if (user) {
      try {
        let stored = JSON.parse(localStorage.getItem("grgpt_saved_accounts") || "[]");
        const token = localStorage.getItem("grgpt_token") || localStorage.getItem("token") || "";

        const currentAccData = {
          id: user._id || "current-user",
          name: user.name || "User",
          email: user.email || "",
          avatar: user.profilePicture || user.avatar || "",
          token: token,
          userObj: user
        };

        const existingIndex = stored.findIndex(
          (acc) => (acc.email && user.email && acc.email.toLowerCase() === user.email.toLowerCase()) || (acc.id && acc.id === user._id)
        );

        let updatedList = [...stored];
        if (existingIndex >= 0) {
          updatedList[existingIndex] = { ...updatedList[existingIndex], ...currentAccData };
        } else {
          updatedList.unshift(currentAccData);
        }

        localStorage.setItem("grgpt_saved_accounts", JSON.stringify(updatedList));
        setSavedAccounts(updatedList);
      } catch (err) {
        console.error("Error managing saved accounts:", err);
      }
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const userFirstName = user.name?.split(" ")[0]?.toUpperCase() || "USER";
  const userEmail = user.email || `${user.name?.toLowerCase().replace(/\s+/g, "")}@gmail.com`;

  // Profile Image Upload Handler
  const handleCameraClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Convert image to Base64 data URL for preview & local storage
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64Image = reader.result;
      try {
        setUploading(true);

        // Update Redux state immediately
        const updatedUser = { ...user, profilePicture: base64Image, avatar: base64Image };
        dispatch(setUser(updatedUser));

        // Attempt API sync if update endpoint exists
        try {
          await API.put("/user/account", { profilePicture: base64Image });
        } catch (apiErr) {
          console.warn("Backend image sync notice:", apiErr?.message);
        }

        setUploading(false);
      } catch (err) {
        console.error("Profile picture update error:", err);
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  // Switch to another saved account
  const handleSwitchAccount = (account) => {
    if (account.email?.toLowerCase() === user.email?.toLowerCase()) {
      return; // Already active
    }

    if (account.token) {
      localStorage.setItem("grgpt_token", account.token);
      localStorage.setItem("token", account.token);
    }
    dispatch(setUser(account.userObj || { name: account.name, email: account.email, profilePicture: account.avatar }));
    
    onClose();
    window.location.reload();
  };

  // Add another account -> navigate to login
  const handleAddAccount = () => {
    onClose();
    navigate("/login?mode=add_account");
  };

  // Sign out of all accounts
  const handleSignOutAll = async () => {
    try {
      await API.post("/auth/logout");
    } catch (e) {}
    localStorage.removeItem("grgpt_token");
    localStorage.removeItem("token");
    localStorage.removeItem("grgpt_saved_accounts");
    dispatch(clearUser());
    onClose();
    navigate("/login");
  };

  const handleManageAccount = () => {
    onClose();
    if (user.email?.includes("@gmail.com")) {
      window.open("https://myaccount.google.com/", "_blank", "noopener,noreferrer");
    } else {
      navigate("/account");
    }
  };

  return (
    <div className="account-switcher-overlay" onClick={onClose}>
      <div
        className="account-switcher-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Header with Active Account Email & Close Button */}
        <div className="asc-header">
          <span className="asc-email-header">{userEmail}</span>
          <button
            type="button"
            className="asc-close-btn"
            onClick={onClose}
            aria-label="Close"
            title="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Profile Avatar & Greeting & Manage Button */}
        <div className="asc-profile-section">
          <div className="asc-avatar-wrapper">
            {/* Google-style segmented 4-color ring */}
            <div className="asc-google-ring">
              <div className="asc-avatar-inner-ring">
                <div className="asc-avatar-circle">
                  {user.profilePicture || user.avatar ? (
                    <img
                      src={user.profilePicture || user.avatar}
                      alt={user.name || "User"}
                      className="asc-avatar-img"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const fallback = e.currentTarget.parentElement?.querySelector(".asc-avatar-fallback");
                        if (fallback) fallback.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <span
                    className="asc-avatar-initial asc-avatar-fallback"
                    style={{ display: user.profilePicture || user.avatar ? "none" : "flex" }}
                  >
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </span>
                </div>
              </div>
            </div>

            {/* Small camera badge button */}
            <button
              type="button"
              className="asc-camera-btn"
              onClick={handleCameraClick}
              title="Change profile photo"
              disabled={uploading}
            >
              <Camera size={12} />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept="image/*"
              onChange={handleFileChange}
            />
          </div>

          <h3 className="asc-greeting">Hi, {userFirstName}!</h3>

          <button
            type="button"
            className="asc-manage-btn"
            onClick={handleManageAccount}
          >
            Manage your Google Account
          </button>
        </div>

        {/* White Rounded Account List Card Container */}
        <div className="asc-white-card">
          {/* Header Row: Hide / Show more accounts */}
          <button
            type="button"
            className="asc-card-row asc-header-row"
            onClick={() => setShowMoreAccounts((prev) => !prev)}
          >
            <span className="asc-header-title">
              {showMoreAccounts ? "Hide more accounts" : "Show more accounts"}
            </span>
            <div className="asc-chevron-badge">
              {showMoreAccounts ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </div>
          </button>

          {showMoreAccounts && (
            <div className="asc-card-list">
              {/* Account Rows */}
              {savedAccounts.map((acc, index) => {
                return (
                  <div
                    key={acc.id || index}
                    className="asc-card-row asc-account-row"
                    onClick={() => handleSwitchAccount(acc)}
                  >
                    <div className="asc-avatar-badge">
                      {acc.avatar ? (
                        <img
                          src={acc.avatar}
                          alt={acc.name || "User"}
                          className="asc-avatar-img"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            const fallback = e.currentTarget.parentElement?.querySelector(".asc-avatar-fallback");
                            if (fallback) fallback.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <span
                        className="asc-avatar-fallback"
                        style={{ display: acc.avatar ? "none" : "flex" }}
                      >
                        {acc.name?.[0]?.toUpperCase() || "U"}
                      </span>
                    </div>
                    <div className="asc-account-text">
                      <div className="asc-account-name">{acc.name}</div>
                      <div className="asc-account-email">{acc.email}</div>
                    </div>
                  </div>
                );
              })}

              {/* Add another account */}
              <div className="asc-card-row asc-action-row" onClick={handleAddAccount}>
                <div className="asc-action-icon-circle blue">
                  <Plus size={16} className="asc-plus-icon" />
                </div>
                <span className="asc-action-title">Add another account</span>
              </div>

              {/* Sign out of all accounts */}
              <div className="asc-card-row asc-action-row asc-logout-row" onClick={handleSignOutAll}>
                <div className="asc-action-icon-circle gray">
                  <LogOut size={16} className="asc-logout-icon" />
                </div>
                <span className="asc-action-title">Sign out of all accounts</span>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer Links */}
        <div className="asc-footer-links">
          <a
            href="https://policies.google.com/privacy?hl=en"
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
          >
            Privacy Policy
          </a>
          <span className="asc-dot">·</span>
          <a
            href="https://policies.google.com/terms?hl=en"
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
          >
            Terms of Service
          </a>
        </div>
      </div>
    </div>
  );
}
